import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import isString from "lodash/isString";

import SortOutlined from "@mui/icons-material/SortOutlined";
import Divider from "@mui/material/Divider";
import { useTheme } from "@mui/material/styles";
import format from "date-fns/format";
import { useMemo, useState } from "react";
import SelectInput from "../../components/inputs/SelectInput";
import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import { useCommentRendererUtilsHook } from "../../hooks/utils/useCommentRendererUtilsHook";
import EnhancedCommentItem from "./EnhancedCommentItem";

const EnhancedCommentItemRenderer = ({
  loan,
  comments,
  sx,
  px,
  gap = 3,
  suppressEmptyPlaceholder,
  hideSort = false,
  defaultSort = "desc",
  padding = 2,
}) => {
  const [sort, setSort] = useState(defaultSort);

  const { sortedDateKeys, dateKeyToDateMap, sortedGroupedComments } =
    useCommentRendererUtilsHook({ comments, sort });

  if (!isArray(comments) || isEmpty(comments)) {
    if (suppressEmptyPlaceholder) {
      return;
    }
    return (
      <RootContainer gap={gap} sx={sx} padding={padding}>
        <Txt>No comments added</Txt>
      </RootContainer>
    );
  }

  return (
    <RootContainer gap={gap} sx={sx} padding={padding}>
      {hideSort ? null : (
        <Flx
          // end
          ac
          fw
          gap={1}
          sx={{
            mb: -3,
            opacity: 0.65,
            fieldset: {
              border: "none",
            },
            ".MuiSelect-select": {
              padding: "0px",
              fontSize: "12px",
            },
          }}
        >
          <SortOutlined className="thin" sx={{ fontSize: "18px" }} />

          <SelectInput
            value={sort}
            fullWidth={false}
            sx={{ width: "105px" }}
            onChange={setSort}
            options={[
              { label: "Most Recent", value: "desc" },
              { label: "Least Recent", value: "asc" },
            ]}
          />
        </Flx>
      )}

      {sortedDateKeys.map((dateKey) => {
        const originalDate = dateKeyToDateMap[dateKey];
        const displayDate =
          dateKey === "Unknown Date" || !originalDate
            ? "Unknown Date"
            : format(originalDate, "EEEE, M/d/yyyy"); // Use original date object

        return (
          <DateGroup key={dateKey}>
            {/* Header now includes weekday */}
            <CreatedDate>{displayDate}</CreatedDate>

            {/* Render comments for this date */}
            <Flx column gap={2}>
              {sortedGroupedComments[dateKey].map((c) => (
                <EnhancedCommentItem
                  key={c?._id}
                  px={px}
                  comment={c}
                  createdDate={c?.commentDateTime}
                  createdBy={c?.postedBy}
                  htmlString={c?.htmlString}
                  data={c?.data}
                  loan={loan}
                />
              ))}
            </Flx>
          </DateGroup>
        );
      })}
    </RootContainer>
  );
};

const CreatedDate = ({ children }) => {
  const theme = useTheme();
  if (isNil(children)) {
    return null;
  }
  return (
    <Divider
      sx={{
        mt: 2,
        // mb: 2,
        //
        opacity: 0.6,
        mx: 4,
        "&::before, &::after": { borderColor: "#dde1e785" },
      }}
    >
      <Txt
        center
        body2
        sx={{
          fontWeight: 400,
          fontStyle: "italic",
          color: theme.palette.text.secondary,
          fontSize: "11px",
          lineHeight: 2,
        }}
      >
        {children}
      </Txt>
    </Divider>
  );
};

const DateGroup = ({ children }) => {
  return (
    <Flx column gap={1} fw sx={{ mb: 2 }}>
      {children}
    </Flx>
  );
};

const RootContainer = ({ children, padding, sx }) => {
  const styles = useMemo(() => {
    return { p: padding, ...sx };
  }, [padding, sx]);
  return (
    <Flx column fw gap={3} sx={styles}>
      {children}
    </Flx>
  );
};

// Helper function from your CommentItem component
const isoToDate = (isoString) => {
  // Handle empty or non-string values
  if (!isoString || !isString(isoString)) {
    return null;
  }

  // Try to create a date object
  const date = new Date(isoString);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    console.warn("Invalid ISO string format:", isoString);
    return null;
  }

  return date;
};

export default EnhancedCommentItemRenderer;
